import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { getStoreImage, updateStoreImage } from '../../api/waiting.api';
import styles from './StoreImageCropper.module.css';

const ASPECT = 16 / 9;
const OUTPUT_WIDTH = 1280;
const OUTPUT_HEIGHT = 720;
const JPEG_QUALITY = 0.75;

interface Props {
  storeId: string;
}

async function cropToDataUrl(imageSrc: string, area: Area): Promise<string> {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context unavailable');

  ctx.drawImage(
    img,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    OUTPUT_WIDTH,
    OUTPUT_HEIGHT,
  );
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function StoreImageCropper({ storeId }: Props) {
  const [savedImage, setSavedImage] = useState<string | null>(null);
  const [pickedImage, setPickedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!storeId) return;
    setIsLoading(true);
    getStoreImage(storeId)
      .then((res) => setSavedImage(res.storeImage))
      .catch(() => setSavedImage(null))
      .finally(() => setIsLoading(false));
  }, [storeId]);

  const onSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'err', text: '이미지 파일만 업로드 가능합니다.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'err', text: '파일 크기는 10MB 이하여야 합니다.' });
      return;
    }
    setMessage(null);
    const dataUrl = await readFileAsDataURL(file);
    setPickedImage(dataUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!pickedImage || !croppedAreaPixels || isSaving) return;
    setIsSaving(true);
    setMessage(null);
    try {
      const dataUrl = await cropToDataUrl(pickedImage, croppedAreaPixels);
      const res = await updateStoreImage(storeId, dataUrl);
      setSavedImage(res.storeImage);
      setPickedImage(null);
      setMessage({ type: 'ok', text: '매장 이미지가 저장되었습니다.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({
        type: 'err',
        text: err instanceof Error ? err.message : '저장에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPickedImage(null);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = async () => {
    if (!window.confirm('현재 매장 이미지를 삭제하시겠습니까?')) return;
    setIsSaving(true);
    try {
      await updateStoreImage(storeId, null);
      setSavedImage(null);
      setMessage({ type: 'ok', text: '매장 이미지가 삭제되었습니다.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({
        type: 'err',
        text: err instanceof Error ? err.message : '삭제에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  return (
    <div className={styles.container}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.hiddenInput}
        onChange={onSelectFile}
      />

      {isLoading ? (
        <div className={styles.loading}>이미지 정보 불러오는 중...</div>
      ) : pickedImage ? (
        <>
          <div className={styles.cropperBox}>
            <Cropper
              image={pickedImage}
              crop={crop}
              zoom={zoom}
              aspect={ASPECT}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={true}
            />
          </div>
          <div className={styles.zoomRow}>
            <span className={styles.zoomLabel}>확대</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className={styles.zoomRange}
            />
            <span className={styles.zoomValue}>{zoom.toFixed(2)}x</span>
          </div>
          <div className={styles.btnRow}>
            <button type="button" className={styles.btnSecondary} onClick={handleCancel} disabled={isSaving}>
              취소
            </button>
            <button type="button" className={styles.btnPrimary} onClick={handleSave} disabled={isSaving}>
              {isSaving ? '저장 중...' : '크롭 저장'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.previewBox}>
            {savedImage ? (
              <img src={savedImage} alt="매장 이미지" className={styles.previewImg} />
            ) : (
              <div className={styles.placeholder}>등록된 매장 이미지가 없습니다</div>
            )}
          </div>
          <div className={styles.btnRow}>
            <button type="button" className={styles.btnSecondary} onClick={triggerFileInput} disabled={isSaving}>
              {savedImage ? '이미지 변경' : '이미지 업로드'}
            </button>
            {savedImage && (
              <button type="button" className={styles.btnDanger} onClick={handleRemove} disabled={isSaving}>
                이미지 삭제
              </button>
            )}
          </div>
          <p className={styles.helpText}>
            16:9 비율로 크롭됩니다. 권장 사이즈: 1280×720 이상.<br />
            업로드 후 키오스크 좌측 패널에 즉시 반영됩니다.
          </p>
        </>
      )}

      {message && (
        <div className={message.type === 'ok' ? styles.msgOk : styles.msgErr}>
          {message.text}
        </div>
      )}
    </div>
  );
}
